import React, { useState, useEffect, useCallback, useRef } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import viewImage from "../../assets/images/icons/view.svg";
import Img from "../../assets/images/img.webp";
import { Link, useLocation } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { UI_CONSTANTS } from "../../utils/constants";
import TablePagination from "../../commons/Pagination/TablePagination";
import { applyListState } from "../../utils/listNavigation";

const getDishImageUrl = (image) => {
  if (!image || typeof image !== "string") return Img;
  if (image.startsWith("http")) return image;
  const base = process.env.REACT_APP_IMAGE_URL || "";
  if (!base) return Img;
  return `${base.replace(/\/$/, "")}/${image.replace(/^\//, "")}`;
};

const getDishList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.result)) return payload.result;
  return [];
};

const getRestaurantName = (item) =>
  item?.restaurant?.restaurant_name || item?.restaurant_detail?.restaurant_name || "N/A";

function DishApproval() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [dishes, setDishes] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const restoredRef = useRef(false);

  const getPendingDishes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit,
        ...(search.trim() && { search: search.trim() }),
      });

      const response = await handleApiCall("get", `${endpoints.menuDishAdminReviewList}?${params}`);
      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const list = getDishList(response?.data?.data);
        setDishes(list);
        setTotalPages(response?.data?.data?.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching dish approval list:", error);
      setDishes([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  useEffect(() => {
    getPendingDishes();
  }, [getPendingDishes]);

  useEffect(() => {
    const listState = location.state?.listState;
    if (!listState || restoredRef.current) return;
    restoredRef.current = true;
    applyListState(listState, { setPage, setSearch });
  }, [location.state]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const dishListState = { page, search };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Dish Approval</h3>
            </div>

            <div className="mb-4 px-0 py-2">
              <div className="card-heading d-flex align-items-center mb-3 pb-2">
                <div className="col-md-4 px-0">
                  <div className="form-group my-0">
                    <input
                      className="form-control search-icon left-icon border"
                      placeholder="Search by dish name, restaurant name"
                      value={search}
                      onChange={handleSearchChange}
                    />
                  </div>
                </div>
              </div>

              <div className="col-md-12 tab-content row px-0 mx-0">
                <div className="table-responsive px-0">
                  <table className="table table-striped trim menu-catalogue-table">
                    <thead>
                      <tr>
                        <th width="80">S.no.</th>
                        <th>Dish Name</th>
                        <th>Restaurant Name</th>
                        <th>Description</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="5" className="text-center py-4">
                            <div className="spinner-border" role="status">
                              <span className="sr-only">Loading...</span>
                            </div>
                          </td>
                        </tr>
                      ) : dishes.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center py-4">
                            {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                          </td>
                        </tr>
                      ) : (
                        dishes.map((item, index) => (
                          <tr key={item._id}>
                            <td>{(page - 1) * limit + index + 1}</td>
                            <td>
                              <div className="menu-catalogue-table__dish">
                                <img
                                  src={getDishImageUrl(item.image)}
                                  alt={item.dish_name || "Dish"}
                                  className="menu-catalogue-table__dish-avatar"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = Img;
                                  }}
                                />
                                <span className="menu-catalogue-table__dish-name">
                                  {item.dish_name || "N/A"}
                                </span>
                              </div>
                            </td>
                            <td>{getRestaurantName(item)}</td>
                            <td className="text-center" title={item.description || undefined}>
                              {item.description || "N/A"}
                            </td>
                            <td>
                              <div className="d-flex align-items-center justify-content-center">
                                <Link
                                  to={`/dish-approval/view/${item._id}`}
                                  state={{
                                    from: "/dish-approval",
                                    listState: dishListState,
                                  }}
                                  className="mx-2"
                                >
                                  <img src={viewImage} alt="View" />
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <TablePagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DishApproval;
